import type { ModelFound, TestResult } from "../../../api/types";
import { Pill } from "../../../ui/Pill";
import { cut, TEST_RESULT_WORDS } from "./words";

/** The service's own message is shown as plain text, cut to 300 characters, never as markup (architecture 7.7). */
export const MESSAGE_LIMIT = 300;

const FOUND_WORDS: Array<{ key: keyof ModelFound; label: string; without: string }> = [
  { key: "tool_calls", label: "Tool calls", without: "The agent is unavailable with this model: it needs tool calls." },
  { key: "streaming", label: "Streaming", without: "Answers arrive all at once instead of word by word." },
  { key: "pictures", label: "Pictures", without: "Scans and pictures cannot be read with this model." },
];

/** What Test connection found, in plain words: one of five results, then what the model can do. */
export function FoundList({ found }: { found: ModelFound }) {
  return (
    <ul className="ra-found" aria-label="What the model can do">
      {FOUND_WORDS.map(({ key, label, without }) => (
        <li key={key} data-found={found[key] ? "yes" : "no"}>
          <strong>{label}:</strong> {found[key] ? "yes" : "no"}
          {!found[key] && <span className="muted"> {without}</span>}
        </li>
      ))}
    </ul>
  );
}

interface TestResultViewProps {
  result: TestResult;
  /** Said after a good result and after a bad one, when the test came with a save (a personal key). */
  saved?: { good: string; bad: string };
}

export function TestResultView({ result, saved }: TestResultViewProps) {
  const words = TEST_RESULT_WORDS[result.result];
  const message = cut(result.message.trim(), MESSAGE_LIMIT);
  return (
    <div className="ra-test" role="status" data-result={result.result}>
      <p>
        <Pill>{words.ok ? "Works" : "Does not work"}</Pill> <strong>{words.headline}</strong>
      </p>
      {message && message !== words.headline && <p className="ra-test__message muted">{message}</p>}
      {saved && <p>{words.ok ? saved.good : saved.bad}</p>}
      {words.ok && <FoundList found={result.found} />}
    </div>
  );
}
